import React from 'react';

import { useTranslation } from '@/i18n';
import { SidePanelFormLayout } from '@/components/layout';
import { PermissionGuard } from '@/components/layout/permission/PermissionGuard';
import { LabelInput, TextArea } from '@/components/ui/inputs';
import { PrimaryButton } from '@/components/ui/buttons/PrimaryButton';
import { IconScout } from '@/components/ui/images/IconScout';
import { Text } from '@/components/ui/text/Text';
import { colors } from '@/assets/styles/colors';
import { formatPersonName } from '@/utils';
import type { IncidentManagementItem } from '@/api/domains/employeeIncident';
import { useIncidentResolution } from '@/hooks/attendance/useIncidentResolution';


const COMMENT_MAX_LENGTH = 100;

interface IncidentManagementDetailPanelProps {
  record: IncidentManagementItem;
  // Aprobación exitosa: cierra el panel y refresca el listado (los contadores
  // se recalculan con la misma respuesta, no requiere llamada aparte).
  onApproved?: () => void;
  // Rechazo exitoso: mismo comportamiento que la aprobación (ASIS-5248).
  onRejected?: () => void;
  // Conflicto de concurrencia (la incidencia ya fue resuelta por otro usuario):
  // el listado quedó desactualizado, por lo que también se cierra y refresca.
  onConflict?: () => void;
}

/**
 * Panel lateral con el detalle de una incidencia de marcaje
 * (ASIS-5246 / REXASIS-004). Recibe la fila ya cargada por el listado
 * (ASIS-5244) vía props; no realiza ninguna petición adicional al abrir.
 *
 * Estado Pendiente: el campo "Comentario aprobador" se vuelve editable y
 * obligatorio, y los botones Aprobar (REXASIS-005) y Rechazar (REXASIS-006)
 * se habilitan una vez que hay comentario, compartiendo el mismo estado.
 * Estados ya resueltos: todo permanece de solo lectura, igual que antes.
 */
export const IncidentManagementDetailPanel: React.FC<IncidentManagementDetailPanelProps> = ({
  record,
  onApproved,
  onRejected,
  onConflict,
}) => {
  const { t: translate } = useTranslation();

  const {
    comment,
    setComment,
    canSubmit,
    isSubmitting,
    handleApprove,
    handleReject,
  } = useIncidentResolution({ incidentId: record.incidentId, onApproved, onRejected, onConflict });

  const isPending = record.status === translate('attendance:incidentManagement.status.pendiente');
  const isApproved = record.status === translate('attendance:incidentManagement.status.aprobado');
  const isRejected = record.status === translate('attendance:incidentManagement.status.rechazado');
  const approvedByLabel = record.approvedBy === '-'
    ? record.approvedBy
    : formatPersonName(record.approvedBy);

  return (
    <SidePanelFormLayout data-testid="incident-management-detail-panel">
      <SidePanelFormLayout.Fields>
        <LabelInput
          label={translate('attendance:incidentManagement.detail.incidentId')}
          value={String(record.incidentId)}
          disabled
          readOnly
          inputClassName="!text-base"
          data-testid="incident-detail-id"
        />
        <LabelInput
          label={translate('attendance:incidentManagement.columns.status')}
          value={record.status}
          disabled
          readOnly
          inputClassName="!text-base"
          data-testid="incident-detail-status"
        />
        <LabelInput
          label={translate('attendance:incidentManagement.detail.rut')}
          value={record.taxId}
          disabled
          readOnly
          inputClassName="!text-base"
          data-testid="incident-detail-tax-id"
        />
        <LabelInput
          label={translate('attendance:incidentManagement.columns.employee')}
          value={formatPersonName(record.employeeName).toUpperCase()}
          disabled
          readOnly
          inputClassName="!text-base"
          data-testid="incident-detail-employee"
        />
        <LabelInput
          label={translate('attendance:incidentManagement.columns.incidentDate')}
          value={record.incidentDate}
          disabled
          readOnly
          inputClassName="!text-base"
          iconRight={<IconScout name="calendar" size={24} color={colors.important.main} />}
          data-testid="incident-detail-incident-date"
        />
        <LabelInput
          label={translate('attendance:incidentManagement.columns.updatedDate')}
          value={record.updatedDate}
          disabled
          readOnly
          inputClassName="!text-base"
          iconRight={<IconScout name="calendar" size={24} color={colors.important.main} />}
          data-testid="incident-detail-updated-date"
        />
        <LabelInput
          label={translate('attendance:incidentManagement.columns.markingDate')}
          value={record.markingDate}
          disabled
          readOnly
          inputClassName="!text-base"
          iconRight={<IconScout name="calendar" size={24} color={colors.feedback.success300} />}
          data-testid="incident-detail-marking-date"
        />
        <LabelInput
          label={translate('attendance:incidentManagement.detail.hora')}
          value={record.markingTime}
          disabled
          readOnly
          inputClassName="!text-base"
          iconRight={<IconScout name="clockThree" size={24} color={colors.important.main} />}
          data-testid="incident-detail-marking-time"
        />
        <LabelInput
          label={translate('attendance:incidentManagement.columns.event')}
          value={record.eventName}
          disabled
          readOnly
          inputClassName="!text-base"
          data-testid="incident-detail-event"
        />
        <LabelInput
          label={translate('attendance:incidentManagement.columns.location')}
          value={record.locationName}
          disabled
          readOnly
          inputClassName="!text-base"
          data-testid="incident-detail-location"
        />
        {(isApproved || isRejected) && (
          <LabelInput
            label={translate(isRejected
              ? 'attendance:incidentManagement.detail.rejectedBy'
              : 'attendance:incidentManagement.columns.approvedBy')}
            value={approvedByLabel}
            disabled
            readOnly
            inputClassName="!text-base"
            data-testid="incident-detail-approved-by"
          />
        )}
        <div className="flex flex-col gap-1">
          <Text variant="body-regular" color={colors.grays.neutral33}>
            {translate('attendance:incidentManagement.columns.requesterComment')}
          </Text>
          <TextArea
            value={record.requesterComment === '-' ? '' : record.requesterComment}
            disabled
            rows={2}
            showCharacterCount
            maxLength={COMMENT_MAX_LENGTH}
            data-testid="incident-detail-requester-comment"
          />
        </div>
        <div className="flex flex-col gap-1">
          <Text variant="body-regular" color={colors.grays.neutral33}>
            {translate('attendance:incidentManagement.columns.approverComment')}
            {isPending && <span className="ml-1">*</span>}
          </Text>
          {isPending ? (
            <TextArea
              value={comment}
              onChange={setComment}
              placeholder={translate('attendance:incidentManagement.detail.approverCommentPlaceholder')}
              rows={2}
              showCharacterCount
              maxLength={COMMENT_MAX_LENGTH}
              data-testid="incident-detail-approver-comment"
            />
          ) : (
            <TextArea
              value={record.approverComment === '-' ? '' : record.approverComment}
              placeholder={translate('attendance:incidentManagement.detail.approverCommentPlaceholder')}
              disabled
              rows={2}
              showCharacterCount
              maxLength={COMMENT_MAX_LENGTH}
              data-testid="incident-detail-approver-comment"
            />
          )}
        </div>
      </SidePanelFormLayout.Fields>

      {/*
        Solo estado Pendiente: acciones Aprobar (REXASIS-005) y Rechazar
        (REXASIS-006). Ambos botones se habilitan únicamente cuando hay
        comentario del aprobador, según criterio de aceptación de la tarjeta,
        y comparten el mismo estado de envío (isSubmitting) para evitar que
        se disparen ambas acciones a la vez.
      */}
      {isPending && (
        <SidePanelFormLayout.Footer>
          <div className="flex w-full gap-3">
            <PermissionGuard action="edit">
              <PrimaryButton
                type="button"
                colorVariant="success"
                customVariant={{
                  default: { textColor: colors.grays.neutralFF },
                  hover: { textColor: colors.grays.neutralFF },
                  pressed: { textColor: colors.grays.neutralFF },
                  focus: { textColor: colors.grays.neutralFF },
                }}
                fullWidth
                disabled={!canSubmit}
                isLoading={isSubmitting}
                onClick={handleApprove}
                data-testid="incident-detail-approve"
              >
                {translate('attendance:incidentManagement.detail.approve')}
              </PrimaryButton>
            </PermissionGuard>
            <PermissionGuard action="edit">
              <PrimaryButton
                type="button"
                colorVariant="error"
                customVariant={{
                  default: { textColor: colors.grays.neutralFF },
                  hover: { textColor: colors.grays.neutralFF },
                  pressed: { textColor: colors.grays.neutralFF },
                  focus: { textColor: colors.grays.neutralFF },
                }}
                fullWidth
                disabled={!canSubmit}
                isLoading={isSubmitting}
                onClick={handleReject}
                data-testid="incident-detail-reject"
              >
                {translate('attendance:incidentManagement.detail.reject')}
              </PrimaryButton>
            </PermissionGuard>
          </div>
        </SidePanelFormLayout.Footer>
      )}
    </SidePanelFormLayout>
  );
};

export default IncidentManagementDetailPanel;
